/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import {
  Container,
  Button,
  Spinner,
  Alert,
  ToastContainer,
  Toast,
  Modal,
  Form,
  Row,
  Col,
} from 'react-bootstrap';
import SectionBlock from './SectionBlock';
import type {
  Question,
  QuestionGroup,
  QuestionSection,
  ScoringRule,
} from '../../types/types';
import { useQuestionnaireData } from '../../hooks/useQuestionnaireData';
import { useParams } from 'react-router-dom';
import QuestionEditorModal from './QuestionEditorModal';
import SectionEditorModal from './SectionEditorModal';
import { v4 as uuidv4 } from 'uuid';
import { InlineEditText } from '../common/InlineTextEdit';
import { supabase } from '../../lib/supabase';
import { captureSupabaseError } from '../../lib/sentry';
import { tracking } from '../../util/tracking';
import { gToastPosition, gToastSentiment } from '../../util/constst';

const QuestionnaireBuilder = () => {
  const { templateId } = useParams<{ templateId: string }>();

  const { template, setTemplate, loading, error, saving, saveTemplate } =
    useQuestionnaireData(templateId ?? '');

  // 🔹 State
  const [scoringRules, setScoringRules] = useState<ScoringRule[]>([]);
  const [editingSection, setEditingSection] = useState<QuestionSection | null>(
    null
  );
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [activeSectionForQuestion, setActiveSectionForQuestion] =
    useState<QuestionSection | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [doSave, setDoSave] = useState(false);
  const [creatingSection, setCreatingSection] = useState<boolean>(false);
  const creatingNewQuestion = useRef<boolean>(false);
  const [ruleForNewQuestion, setRuleForNewQuestion] = useState<any>(null);

  // 🔹 Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [sectionToDelete, setSectionToDelete] =
    useState<QuestionSection | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 🔹 Fetch scoring rules for this template
  useEffect(() => {
    const fetchScoringRules = async () => {
      if (!templateId) return;
      const { data, error } = await supabase
        .from('scoring_rules')
        .select('*')
        .eq('template_id', templateId);

      if (error) {
        console.error('Error fetching scoring rules:', error);
      } else {
        setScoringRules(data || []);
      }
    };
    fetchScoringRules();
  }, [templateId]);

  useEffect(() => {
    console.log('template changed', template);
    if (doSave) {
      handleSave();
      setDoSave(false);
    }
  }, [template, doSave]);

  if (loading) return <Spinner animation="border" />;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!template) return <Alert variant="warning">Template not found</Alert>;

  const handleSave = async () => {
    const { error } = await saveTemplate(template);
    if (creatingNewQuestion.current) {
      creatingNewQuestion.current = false;
      handleSaveScoringRule(ruleForNewQuestion);
      setRuleForNewQuestion(null);
    }
    if (!error) {
      setToastMessage('Template saved successfully');
      setShowToast(true);
    }
  };

  const addSection = () => {
    if (!template?.sections) return;

    const newSection: QuestionSection = {
      id: uuidv4(),
      key: `section-${template.sections.length}`,
      title: 'New Section',
      ordinal: template.sections.length,
      questions: [],
      description: {},
      exclude_from_score: false,
    };

    setEditingSection(newSection);
    setCreatingSection(true);
  };

  const updateSection = (updatedSection: QuestionSection) => {
    if (!template?.sections) return;

    const newtemplateSections = creatingSection
      ? [...template.sections, updatedSection]
      : template.sections.map((s) =>
          s.id === updatedSection.id ? updatedSection : s
        );
    console.log('updateSection:setting template:', newtemplateSections);
    setTemplate({
      ...template,
      sections: newtemplateSections,
    });
    setCreatingSection(false);
    setDoSave(true);
  };

  // 🔹 Handle delete confirmation
  const handleDeleteRequest = (section: QuestionSection) => {
    setSectionToDelete(section);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (!sectionToDelete || !template?.sections) return;
    try {
      setDeleting(true);
      const { error } = await supabase
        .from('question_sections')
        .update({ deleted: true })
        .eq('id', sectionToDelete.id);

      if (error) throw error;

      console.log('confirmDelete:setting template:');
      setTemplate({
        ...template,
        sections: template.sections.filter((s) => s.id !== sectionToDelete.id),
      });

      setToastMessage(`Deleted section: ${sectionToDelete.title}`);
      setShowToast(true);
    } catch (err) {
      captureSupabaseError(err, { op: 'section.delete' });
      console.error('Failed to delete section:', err);
      alert('Failed to delete section. Check console for details.');
    } finally {
      setDeleting(false);
      setShowConfirmModal(false);
      setSectionToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setSectionToDelete(null);
  };

  const editSection = (section: QuestionSection) => {
    setEditingSection(section);
  };

  const handleEditQuestion = (section: QuestionSection, question: Question) => {
    setActiveSectionForQuestion(section);
    setEditingQuestion(question);
  };

  // 🧮 Handle scoring rule upsert
  const handleSaveScoringRule = async (rule: any) => {
    // afeguard to prevent saving while creating new question
    if (creatingNewQuestion.current) {
      setRuleForNewQuestion(rule);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('scoring_rules')
        .upsert(
          {
            id: rule.id,
            question_id: rule.question_id,
            template_id: rule.template_id,
            section_id: rule.section_id,
            scoring_logic: rule.scoring_logic,
            weight: rule.weight,
          },
          { onConflict: 'question_id' }
        )
        .select('*')
        .single();

      if (error) throw error;

      setScoringRules((prev) => {
        const exists = prev.find((r) => r.question_id === rule.question_id);
        if (exists) {
          return prev.map((r) =>
            r.question_id === rule.question_id ? data : r
          );
        }
        return [...prev, data];
      });

      setToastMessage('Scoring rule saved');
      setShowToast(true);
    } catch (err) {
      captureSupabaseError(err, { op: 'scoringRule.save' });
      console.error('Error saving scoring rule:', err);
      alert('Failed to save scoring rule');
    }
  };

  // 🗂️ Save all question groups for a section
  const handleSaveQuestionGroups = async (
    sectionId: string,
    groups: QuestionGroup[]
  ) => {
    try {
      // 1) Fetch existing groups for this section
      const { data: existing, error: fetchError } = await supabase
        .from('question_groups')
        .select('id')
        .eq('section_id', sectionId);

      if (fetchError) throw fetchError;

      const existingIds = existing?.map((g) => g.id) ?? [];
      const incomingIds = groups.map((g) => g.id);

      // 2) Determine which are deleted (exist in DB but not in editor)
      const deletedIds = existingIds.filter((id) => !incomingIds.includes(id));

      // 3) Delete removed ones
      if (deletedIds.length > 0) {
        await supabase.from('question_groups').delete().in('id', deletedIds);

        // 🆕 3b) Clean orphaned questions: remove group_id
        await supabase
          .from('questions')
          .update({ group_id: null })
          .in('group_id', deletedIds);
      }

      // 4) Upsert added/updated groups
      if (groups.length > 0) {
        await supabase.from('question_groups').upsert(
          groups.map((g) => ({
            id: g.id,
            title: g.title,
            description: g.description ?? null,
            section_id: sectionId,
            sort_order: g.sort_order ?? 0,
          }))
        );
      }

      // 5) Update template locally so UI syncs correctly
      setTemplate((prev) => {
        if (!prev?.sections) return prev;
        return {
          ...prev,
          sections: prev.sections.map((s) => {
            if (s.id !== sectionId) return s;

            // Clear group_id for questions that referenced deleted groups
            const updatedQuestions = s.questions.map((q) => ({
              ...q,
              group_id: deletedIds.includes(q.group_id!) ? null : q.group_id,
            }));

            return {
              ...s,
              question_groups: [...groups],
              questions: updatedQuestions,
            };
          }),
        };
      });

      // 6) Also update activeSectionForQuestion if it's the same section
      if (activeSectionForQuestion) {
        setActiveSectionForQuestion((prev) => {
          if (!prev) return prev;

          const updatedQuestions = prev.questions.map((q) => ({
            ...q,
            group_id: deletedIds.includes(q.group_id!) ? null : q.group_id,
          }));

          return {
            ...prev,
            question_groups: [...groups],
            questions: updatedQuestions,
          };
        });
      }
    } catch (err) {
      captureSupabaseError(err, { op: 'questionGroups.save' });
      console.error('Error saving question groups:', err);
      alert('Failed to save groups');
    }
  };

  const handleReorderSection = (newOrder: number, sectionId: string) => {
    if (!template.sections) {
      console.warn('No sections found');
      return;
    }

    const sectionIndex = template.sections.findIndex((q) => q.id === sectionId);

    if (sectionIndex === -1) {
      console.warn('Section not found:', sectionId);
      return;
    }

    const reorderedSections = [...template.sections];
    const [movedSection] = reorderedSections.splice(sectionIndex, 1);
    reorderedSections.splice(newOrder, 0, movedSection);

    // Update ordinals to match new positions
    const updatedSections = reorderedSections.map((q, index) => ({
      ...q,
      ordinal: index,
    }));
    console.log('handleReorderSection:setting template:', updatedSections);
    setTemplate({ ...template, sections: updatedSections });
    setDoSave(true);
  };

  const handleAddNewQuestion = (section: QuestionSection) => {
    creatingNewQuestion.current = true;
    const newQuestion: Question = {
      id: uuidv4(),
      key: `q-${section.key}-${section.questions.length}`,
      ordinal: section.questions.length,
      type: 'text',
      help_text: {},
      required: false,
      hidden: false,
      options: [],
      section_id: section.id,
    };

    setActiveSectionForQuestion(section);
    setEditingQuestion(newQuestion); // Opens modal
  };

  return (
    <Container className="py-4">
      <Row>
        <Col>
          <Button variant="secondary" href="/questionnaires" className="mb-2">
            &larr; Back to Templates
          </Button>
        </Col>
      </Row>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 style={{ minHeight: '30px' }}>
          <InlineEditText
            value={template.title}
            onSave={(val) => {
              console.log('InlineEditText:setting template:', val);
              setTemplate({ ...template, title: val });
              setDoSave(true);
            }}
            showIcon
            inputClassName="minWidth-600"
          />
        </h2>
        <div className="d-flex align-items-center">
          <Form.Check
            type="switch"
            id="publish-switch"
            label="Published"
            className="me-3"
            checked={template.status === 'published'}
            onChange={(e) => {
              console.log('Publish switch changed:', e.target.checked);
              setTemplate({
                ...template,
                status: e.target.checked ? 'published' : 'draft',
              });
              setDoSave(true);
            }}
          />
          <Button
            variant="outline-secondary"
            className="me-2"
            disabled={saving}
            onClick={() => {
              const id = templateId ?? template?.id;
              if (!id) return;
              window.open(
                `/questionnaire/${id}`,
                '_blank',
                'noopener,noreferrer'
              );
            }}
          >
            Preview
          </Button>{' '}
          <Button
            variant="success"
            onClick={() => {
              tracking.log('UI', 'questionnaire_save', {
                templateId: templateId ?? template?.id,
              });
              handleSave();
            }}
            disabled={saving}
          >
            Save Template
          </Button>
        </div>
      </div>

      <div className="mb-2" style={{ minHeight: '30px', minWidth: '400px' }}>
        <InlineEditText
          value={template.description || ''}
          onSave={(val) => {
            console.log('InlineEditText:setting template description:', val);
            setTemplate({ ...template, description: val });
            setDoSave(true);
          }}
          showIcon
          inputClassName="minWidth-600"
        />
      </div>

      {[...(template.sections ?? [])]
        .sort((a, b) => a.ordinal - b.ordinal)
        .map((section) => (
          <SectionBlock
            key={section.id}
            section={section}
            template={template}
            updateSection={updateSection}
            deleteSection={() => handleDeleteRequest(section)}
            editSection={editSection}
            onAddQuestion={() => handleAddNewQuestion(section)} // NEW
            onEditQuestion={(question) => handleEditQuestion(section, question)}
            scoringRules={scoringRules}
            onReorder={handleReorderSection}
            saving={saving}
          />
        ))}

      <Button variant="outline-primary" className="mt-3" onClick={addSection}>
        <i className="bi bi-plus me-2"></i> Add Section
      </Button>

      {/* ✅ Section Editor Modal */}
      {editingSection && (
        <SectionEditorModal
          section={editingSection}
          onSave={(updatedSection) => {
            updateSection(updatedSection);
            setEditingSection(null);
            setCreatingSection(false);
          }}
          onClose={() => {
            setEditingSection(null);
            setCreatingSection(false);
          }}
        />
      )}

      {/* ✅ Question Editor Modal */}
      {editingQuestion && activeSectionForQuestion && (
        <QuestionEditorModal
          question={editingQuestion}
          section={activeSectionForQuestion}
          templateId={templateId ?? ''}
          existingRule={scoringRules.find(
            (r) => r.question_id === editingQuestion.id
          )}
          onSaveQuestion={(updatedQuestion) => {
            const exists = activeSectionForQuestion.questions.some(
              (q) => q.id === updatedQuestion.id
            );

            const updatedSection = {
              ...activeSectionForQuestion,
              questions: exists
                ? activeSectionForQuestion.questions.map((q) =>
                    q.id === updatedQuestion.id ? updatedQuestion : q
                  )
                : [...activeSectionForQuestion.questions, updatedQuestion],
            };

            updateSection(updatedSection);
          }}
          onSaveScoringRule={handleSaveScoringRule}
          onSaveQuestionGroups={handleSaveQuestionGroups}
          onClose={() => {
            setEditingQuestion(null);
            setActiveSectionForQuestion(null);
          }}
        />
      )}

      {/* 🗑️ Delete Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={cancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{' '}
          <strong>{sectionToDelete?.title}</strong>?<br />
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={cancelDelete}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position={gToastPosition} className="p-3">
        <Toast
          bg={gToastSentiment}
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={3000}
          autohide
        >
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
};

export default QuestionnaireBuilder;
